import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogTitle,
  Divider,
  Fade,
  IconButton,
  PaperProps,
  Slide,
  Typography
} from "@mui/material";
import Box from "@mui/material/Box";
import { TransitionProps } from "@mui/material/transitions";
import { useDeviceDetach } from "hooks/useDeviceDetach";
import React, { CSSProperties, useMemo } from "react";

type PopupProps = {
  children?: React.ReactNode;
  show?: boolean;
  label: string;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  onClose: () => void;
  zIndex?: number;
  fullHeight?: boolean;
  fullScreen?: boolean;
  PaperProps?: Partial<PaperProps<React.ElementType>>;
  TransitionComponent?: React.JSXElementConstructor<any>;
};

const SlideUp = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const Popup: (props: PopupProps) => JSX.Element = (props) => {
  const { isMobile } = useDeviceDetach();

  const transitionComponent = useMemo(() => {
    if (props.TransitionComponent) {
      return props.TransitionComponent;
    }

    if (isMobile) {
      return SlideUp
    }

    return Fade
  }, [isMobile, props.TransitionComponent])

  const fullscreen = useMemo(() => {
    return props.fullScreen ?? isMobile
  }, [isMobile, props.fullScreen])

  return (
    <Dialog
      open={props.show ?? false}
      onClose={props.onClose}
      fullWidth
      maxWidth={props.maxWidth ?? "lg"}
      fullScreen={fullscreen}
      TransitionComponent={transitionComponent}
      disableEnforceFocus
      sx={{
        backgroundColor: "paper",
        zIndex: props.zIndex ?? 10,
        "& .MuiDialog-paper": isMobile
          ? null
          : {
              display: "flex",
              flexDirection: "column",
              height: props.fullHeight ? "100%" : "auto",
            },
      }}
      PaperProps={{...props.PaperProps, elevation: 0}}
    >
      <DialogTitle>
        <Box sx={styles.toolbarContainer}>
          <Typography
            color="text.secondary"
            sx={styles.toolbarText}
            variant="h6"
          >
            {props.label}
          </Typography>
          <IconButton
            size="large"
            edge="start"
            color="default"
            aria-label="menu"
            sx={styles.toolbarCloseButton}
            onClick={props.onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      {props.children}
    </Dialog>
  );
};

const styles: { [key: string]: CSSProperties } = {
  container: {
    position: "fixed",
    width: "100%",
    height: "100%",
    backgroundColor: "background.default",
    zIndex: 1000,
    transitionProperty: "scale, opacity, top",
    transitionDuration: "0.3s",
    transitionTimingFunction: "ease-in-out",
    display: "flex",
    flexDirection: "column",
  },
  toolbarContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    boxSizing: "border-box",
  },
  toolbarCloseButton: {
    position: "relative",
    left: "10px",
  },
};
